import React, { createContext, useState, useEffect } from 'react';
import authService from '../../services/authService';
import { getAccessToken, clearTokens } from '../../api';
import toast from 'react-hot-toast';
import { getUserFriendlyError, ERROR_MESSAGES } from '../../utils/errorMessages';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check if user is logged in on mount
  useEffect(() => {
    checkAuth();
  }, []);

  /**
   * Check authentication status
   */
  const checkAuth = async () => {
    try {
      // Check if we have a token first
      const token = getAccessToken();
      if (!token) {
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }

      const response = await authService.getCurrentUser();

      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
      } else {
        // API returned but user is not authenticated
        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Only clear tokens if it's a 401 error (unauthorized)
      // Don't clear on network errors or other issues
      if (error.response?.status === 401 || error.message?.includes('Invalid token')) {
        clearTokens();
        setUser(null);
        setIsAuthenticated(false);
      }
      // For other errors (network issues, etc.), keep the current state
    } finally {
      setLoading(false);
    }
  };

  /**
   * Sign up with email and password
   */
  const signup = async (email, password, fullName) => {
    try {
      const response = await authService.signup({
        email,
        password,
        full_name: fullName
      });

      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        setLoading(false); // Ensure loading is false after successful signup

        // Show success message
        if (response.emailVerificationRequired) {
          toast.success('Account created! Please verify your email.', {
            duration: 5000
          });
        } else {
          toast.success('Account created successfully!');
        }

        return { success: true, user: response.user };
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, 'Failed to create account. Please try again.');
      toast.error(friendlyMessage);
      return { success: false, error: friendlyMessage };
    }
  };

  /**
   * Login with email and password
   */
  const login = async (email, password) => {
    try {
      const response = await authService.login({ email, password });

      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        setLoading(false); // Ensure loading is false after successful login
        toast.success('Welcome back!');
        return { success: true, user: response.user };
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.AUTH.INVALID_CREDENTIALS);
      toast.error(friendlyMessage);
      return { success: false, error: friendlyMessage };
    }
  };

  /**
   * Login with Google OAuth
   */
  const loginWithGoogle = async () => {
    try {
      const response = await authService.googleAuth();

      if (response.success && response.url) {
        // Redirect to Google OAuth
        window.location.href = response.url;
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, 'Failed to start Google sign in. Please try again.');
      toast.error(friendlyMessage);
      return { success: false, error: friendlyMessage };
    }
  };

  /**
   * Handle OAuth callback
   */
  const handleOAuthCallback = async (accessToken, refreshToken) => {
    try {
      console.log('[AuthContext] OAuth callback starting with tokens:', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken
      });

      const response = await authService.handleOAuthCallback({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      console.log('[AuthContext] OAuth callback response:', {
        success: response.success,
        hasUser: !!response.user,
        hasTokens: !!(response.access_token && response.refresh_token)
      });

      if (response.success) {
        setUser(response.user);
        setIsAuthenticated(true);
        setLoading(false); // Ensure loading is false after OAuth success
        console.log('[AuthContext] User authenticated successfully via OAuth');
        toast.success('Successfully logged in with Google!');
        return { success: true, user: response.user };
      } else {
        // Handle case where response exists but success is false
        console.error('[AuthContext] OAuth callback failed:', response.message);
        const friendlyMessage = getUserFriendlyError(response.message, 'Failed to complete Google sign in. Please try again.');
        toast.error(friendlyMessage);
        return { success: false, error: friendlyMessage };
      }
    } catch (error) {
      console.error('[AuthContext] OAuth callback error:', error);
      const friendlyMessage = getUserFriendlyError(error, 'Failed to complete Google sign in. Please try again.');
      toast.error(friendlyMessage);
      return { success: false, error: friendlyMessage };
    }
  };

  /**
   * Logout current user
   */
  const logout = async () => {
    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      toast.success('Logged out successfully');
      return { success: true };
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, 'Failed to sign out. Please try again.');
      toast.error(friendlyMessage);
      return { success: false, error: error.message };
    }
  };

  /**
   * Resend verification email
   */
  const resendVerification = async (email) => {
    try {
      const response = await authService.resendVerification(email);
      
      if (response.success) {
        toast.success('Verification email sent!');
        return { success: true };
      }
    } catch (error) {
      toast.error(error.message || 'Failed to resend verification email');
      return { success: false, error: error.message };
    }
  };

  /**
   * Update user data in context
   */
  const updateUser = (userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }));
  };



  


  const value = {
    user,
    loading,
    isAuthenticated,
    signup,
    login,
    loginWithGoogle,
    handleOAuthCallback,
    logout,
    resendVerification,
    updateUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};